import React, { useState, useEffect, useCallback, useMemo } from "react";
import DOMPurify from "dompurify";
import {
  FileText, Plus, Search, Download, Eye, Edit, Trash2, CheckCircle, AlertCircle, ArrowLeft, Save, X, Loader2, Upload,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import hrService from "../../../api/services/hrService";
import {
  policyService,
  acknowledgmentService,
} from "../../../api/services/complianceService";
import { toast } from "../../../utils/toast";
import { formatDate } from "../../../utils/stringUtils";
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from "../../../utils/formValidation";
import { policyCategoryService } from "../../../api/crm/config/configService";
import {
  exportToCSV,
  downloadFromBackend,
  generateExportFilename,
} from "../../../utils/exportUtils";
import ExportModal from "../../../components/common/ExportModal";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import useUrlTableState from "../../../hooks/useUrlTableState";
import BulkUploadModal from "../../../components/common/BulkUploadModal";
import RichTextEditor from "../../../components/common/RichTextEditor";
import "react-quill/dist/quill.snow.css";

const DESCRIPTION_MAX_CHARS = 6000;

const RICH_TEXT_SANITIZE_OPTIONS = {
  ADD_ATTR: ["style", "class", "target", "rel"],
};

const sanitizeRichTextHtml = (html) =>
  DOMPurify.sanitize(html, RICH_TEXT_SANITIZE_OPTIONS);

const prepareDescriptionForDisplay = (description) => {
  if (!description || !String(description).trim()) {
    return '<p class="text-gray-500">No description available</p>';
  }
  const trimmed = String(description).trim();
  if (/<[a-z][\s\S]*>/i.test(trimmed)) {
    return sanitizeRichTextHtml(trimmed);
  }
  const escaped = trimmed
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\n/g, "<br>");
  return `<p>${escaped}</p>`;
};

const RICH_TEXT_DISPLAY_CLASS =
  "text-sm text-gray-700 leading-relaxed break-words " +
  "[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:text-gray-900 " +
  "[&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:text-gray-900 " +
  "[&_h3]:text-lg [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:text-gray-900 " +
  "[&_h4]:text-base [&_h4]:font-bold [&_h4]:mb-2 [&_h4]:text-gray-900 " +
  "[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-4 [&_ul]:mt-2 " +
  "[&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-4 [&_ol]:mt-2 " +
  "[&_li]:mb-1 [&_li]:pl-1 " +
  "[&_p]:mb-4 [&_p]:leading-relaxed [&_p:last-child]:mb-0 " +
  "[&_strong]:font-bold [&_strong]:text-gray-900 " +
  "[&_em]:italic [&_u]:underline [&_s]:line-through " +
  "[&_blockquote]:border-l-4 [&_blockquote]:border-gray-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4 " +
  "[&_a]:text-[#0F2942] [&_a]:underline [&_a]:hover:text-blue-800 " +
  "[&_pre]:bg-gray-50 [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-4 " +
  "[&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono";

const PolicyLibrary = ({ userRole, initialMode, id, onClose, isDrawer }) => {
  const queryClient = useQueryClient();
  const [filteredPolicies, setFilteredPolicies] = useState([]);

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterCategory: 'all',
    filterStatus: 'all',
    filterStartDate: '',
    filterEndDate: ''
  }), []);

  const {
    filters: policyFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updatePolicyFilter,
    updatePage: updatePolicyPage,
    updateLimit: updatePolicyLimit,
    resetFilters: resetPolicyFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterCategory = policyFilters.filterCategory || 'all';
  const setFilterCategory = useCallback((category) => {
    updatePolicyFilter({ filterCategory: category });
  }, [updatePolicyFilter]);

  const filterStatus = policyFilters.filterStatus || 'all';
  const setFilterStatus = useCallback((status) => {
    updatePolicyFilter({ filterStatus: status });
  }, [updatePolicyFilter]);

  const startDate = policyFilters.filterStartDate || '';
  const endDate = policyFilters.filterEndDate || '';

  const setDateRange = useCallback((range) => {
    updatePolicyFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updatePolicyFilter]);

  const setStartDate = useCallback((date) => {
    updatePolicyFilter({ filterStartDate: date });
  }, [updatePolicyFilter]);

  const setEndDate = useCallback((date) => {
    updatePolicyFilter({ filterEndDate: date });
  }, [updatePolicyFilter]);

  const page = policyFilters.page;
  const limit = policyFilters.limit;

  const setPage = useCallback((p) => {
    updatePolicyPage(p);
  }, [updatePolicyPage]);

  const setLimit = useCallback((l) => {
    updatePolicyLimit(l);
  }, [updatePolicyLimit]);

  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    description: "",
    effectiveDate: "",
    status: "draft",
  });
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [originalEffectiveDate, setOriginalEffectiveDate] = useState(null);

  // Page reset is handled by useUrlTableState

  // Export State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Bulk Delete State
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Helper function to normalize tags
  const normalizeTags = (tags) => {
    if (Array.isArray(tags)) return tags.filter(Boolean);
    if (typeof tags === "string") {
      return tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
    }
    return [];
  };

  // React Query: Load policy categories
  const { data: categoriesData } = useQuery({
    queryKey: ['policyCategoriesDropdown'],
    queryFn: async () => {
      try {
        const res = await policyCategoryService.getDropdown();
        return res.data || [];
      } catch (error) {
        return [];
      }
    },
    staleTime: 0,
  });

  const categories = categoriesData?.map(c => {
    // Capitalize each word in the category name
    return c.name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }) || [
      "HR Policies",
      "IT & Information Security",
      "Data Protection & Privacy",
      "Software Development & Engineering",
      "Cloud & Infrastructure",
      "Cybersecurity",
      "DevOps & Deployment",
      "Code of Conduct",
      "Finance & Expense Policies",
      "Project Management",
      "Product Management",
      "Research & Innovation",
      "Training & Development",
      "Confidentiality & NDA",
      "Compliance & Legal",
      "Business Continuity & Disaster Recovery",
      "Other"
    ];


  // React Query: Load departments
  const { data: departments = [] } = useQuery({
    queryKey: ['policyDepartments'],
    queryFn: async () => {
      try {
        // Prefer exact department values from Employee records
        const resEmp = await hrService.getEmployeeDepartmentsDistinct();
        if (resEmp && resEmp.success && Array.isArray(resEmp.data) && resEmp.data.length) {
          return resEmp.data;
        }
        // Fallback to Department master
        const res = await hrService.getDepartments();
        if (res && res.success && res.data) {
          const deptList = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data.departments)
              ? res.data.departments
              : [];
          return deptList
            .map((d) => d?.name || d?.departmentName || d?.label || d?.value)
            .filter(Boolean);
        }
        return [];
      } catch (e) {
        return [];
      }
    },
  });

  // React Query: Load acknowledgment stats
  const { data: acknowledgmentStats } = useQuery({
    queryKey: ['acknowledgmentStats'],
    queryFn: async () => {
      try {
        const statsResp = await acknowledgmentService.getAcknowledgmentStats();
        return statsResp?.data || statsResp || {};
      } catch (e) {
        return {};
      }
    },
  });

  // React Query: Load policies
  const {
    data: policiesResult,
    isLoading: loading,
    isError: isErrorLoading,
    error: loadingError,
  } = useQuery({
    queryKey: ['policies', searchTerm, filterCategory, filterStatus, startDate, endDate, page, limit],
    queryFn: async () => {
      const response = await policyService.getPolicies({
        category: filterCategory !== "all" ? filterCategory : undefined,
        status: filterStatus !== "all" ? filterStatus : undefined,
        search: searchTerm || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page,
        limit,
      });

      // Handle different response structures
      let policiesData = [];
      let pagination = {
        page: page,
        limit: limit,
        total: 0,
        totalPages: 0
      };

      if (response && response.data && Array.isArray(response.data)) {
        policiesData = response.data;
        if (response.pagination) {
          pagination = response.pagination;
        }
      } else if (Array.isArray(response)) {
        policiesData = response;
        pagination.total = response.length;
        pagination.totalPages = Math.ceil(response.length / limit);
      } else if (response && Array.isArray(response.policies)) {
        policiesData = response.policies;
        if (response.pagination) {
          pagination = response.pagination;
        }
      }

      // Fetch acknowledgment stats separately to enrich policies
      let statsData = {};
      try {
        const statsResp = await acknowledgmentService.getAcknowledgmentStats();
        statsData = statsResp?.data || statsResp || {};
      } catch (e) {
      }

      const byPolicy = statsData?.byPolicy || {};

      const merged = (policiesData || []).map((p) => {
        const pid = p.id || p._id || p.policyId;
        const s = pid ? byPolicy[pid] : null;
        const totalFromStats = s?.total ?? undefined;
        const acknowledgedFromStats = s?.acknowledged ?? undefined;
        const totalEmployees = Number(p.totalEmployees ?? totalFromStats ?? 0);
        const acknowledgmentCount = Number(p.acknowledgmentCount ?? acknowledgedFromStats ?? p.acknowledgedEmployees ?? 0);
        const acknowledgmentRate = totalEmployees > 0 ? Math.round((acknowledgmentCount / totalEmployees) * 100) : 0;

        return {
          ...p,
          totalEmployees,
          acknowledgmentCount,
          acknowledgedEmployees: acknowledgmentCount,
          acknowledgmentRate,
          tags: normalizeTags(p.tags),
        };
      });

      return {
        policies: merged,
        pagination: pagination
      };
    },
    enabled: true,
  });

  const policies = policiesResult?.policies || [];
  const pagination = policiesResult?.pagination;

  const clampText = (text = "") => (text || "").trim();
  const isValidStatus = (s) => ["draft", "active", "archived", "under_review", "inactive"].includes(s);

  const getPlainTextFromHtml = useCallback((html = "") => {
    if (typeof document === "undefined") {
      return String(html).replace(/<[^>]*>/g, "");
    }
    const container = document.createElement("div");
    container.innerHTML = html;
    return container.textContent || container.innerText || "";
  }, []);

  const truncateHtmlToPlainTextLimit = useCallback((html = "", maxChars = DESCRIPTION_MAX_CHARS) => {
    if (typeof document === "undefined") {
      return getPlainTextFromHtml(html).slice(0, maxChars);
    }
    const container = document.createElement("div");
    container.innerHTML = html;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let remaining = maxChars;
    const textNodes = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }
    for (const node of textNodes) {
      const content = node.textContent || "";
      if (remaining <= 0) {
        node.textContent = "";
        continue;
      }
      if (content.length <= remaining) {
        remaining -= content.length;
      } else {
        node.textContent = content.slice(0, remaining);
        remaining = 0;
      }
    }
    return container.innerHTML;
  }, [getPlainTextFromHtml]);
  const isPastDate = (d) => {
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dd = new Date(d);
    dd.setHours(0, 0, 0, 0);
    return dd < today;
  };
  const isAfter = (a, b) => {
    if (!a || !b) return false;
    return new Date(a).getTime() > new Date(b).getTime();
  };

  // Helper function for text input key press - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  const validateForm = (data) => {
    const errors = {};
    // Use originalEffectiveDate from state when validating
    const title = clampText(data.title);
    if (!title) errors.title = "Title is required";
    else if (!VALID_TEXT_REGEX.test(title)) errors.title = "Title can only contain alphanumeric characters and approved symbols";
    else if (title.length < 3) errors.title = "Title must be at least 3 characters";
    else if (title.length > 100) errors.title = "Title must be at most 100 characters";

    if (!data.category) errors.category = "Category is required";

    const descriptionPlain = clampText(getPlainTextFromHtml(data.description));
    if (!descriptionPlain) errors.description = "Description is required";
    else if (descriptionPlain.length < 20) errors.description = "Minimum 20 characters required";
    else if (descriptionPlain.length > DESCRIPTION_MAX_CHARS) errors.description = `Maximum ${DESCRIPTION_MAX_CHARS} characters allowed`;

    if (!data.effectiveDate) errors.effectiveDate = "Effective date is required";
    else if (originalEffectiveDate) {
      // When editing, ensure the new date is not before the original effective date
      const selectedDate = new Date(data.effectiveDate);
      const originalDate = new Date(originalEffectiveDate);
      selectedDate.setHours(0, 0, 0, 0);
      originalDate.setHours(0, 0, 0, 0);
      if (selectedDate < originalDate) {
        errors.effectiveDate = "Effective date cannot be earlier than the original effective date";
      }
    } else {
      // When creating, ensure the date is not in the past
      if (isPastDate(data.effectiveDate)) {
        errors.effectiveDate = "Effective date cannot be in the past";
      }
    }

    if (!isValidStatus(data.status)) errors.status = "Invalid status";

    return errors;
  };

  useEffect(() => {
    if (initialMode === "create") {
      handleCreatePolicy();
    } else if (id && (initialMode === "edit" || initialMode === "view")) {
      const policy = policies.find(p => (p.id || p._id || p.policyId) === id);
      if (policy) {
        if (initialMode === "edit") handleEditPolicy(policy);
        else handleViewDetails(policy);
      }
    }
  }, [initialMode, id, policies.length]);

  useEffect(() => {
    // Live validation when form is open
    if (showPolicyForm) {
      setFormErrors(validateForm(formData));
    }
  }, [formData, showPolicyForm, originalEffectiveDate]);

  // Page reset is handled by useUrlTableState

  // Filter policies based on search term (client-side filtering for additional search)
  useEffect(() => {
    let filtered = policies || [];

    if (searchTerm) {
      filtered = filtered.filter(
        (policy) =>
          policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getPlainTextFromHtml(policy.description).toLowerCase().includes(searchTerm.toLowerCase()) ||
          (policy.tags || []).some((tag) =>
            tag.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    if (filterCategory !== "all") {
      filtered = filtered.filter(
        (policy) => policy.category === filterCategory
      );
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter((policy) => policy.status === filterStatus);
    }

    setFilteredPolicies(filtered);
  }, [policies, searchTerm, filterCategory, filterStatus]);

  const handleCreatePolicy = () => {
    setEditingPolicy(null);
    setOriginalEffectiveDate(null); // Clear original date for new policy
    setFormData({
      title: "",
      category: "",
      description: "",
      effectiveDate: "",
      status: "draft",
    });
    setShowPolicyForm(true);
  };

  // Helper function to format date for date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      // Get local date in YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return "";
    }
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    const originalDate = formatDateForInput(policy.effectiveDate);
    setOriginalEffectiveDate(originalDate); // Store original effective date
    setFormData({
      title: policy.title || "",
      category: policy.category || "",
      description: policy.description || "",
      effectiveDate: originalDate,
      status: policy.status || "draft",
    });
    setShowPolicyForm(true);
  };

  // React Query: Create/Update policy mutation
  const savePolicyMutation = useMutation({
    mutationFn: async ({ policyId, policyData }) => {
      if (policyId) {
        return await policyService.updatePolicy(policyId, policyData);
      }
      return await policyService.createPolicy(policyData);
    },
    onSuccess: (savedPolicy, variables) => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['acknowledgmentStats'] });
      toast.success(variables.policyId ? "Policy updated successfully" : "Policy created successfully");
      setShowPolicyForm(false);
      setEditingPolicy(null);
      setFormData({
        title: "",
        category: "",
        description: "",
        effectiveDate: "",
        status: "draft",
      });
      setFormErrors({});
      setTouched({});
      setOriginalEffectiveDate(null);
    },
    onError: (error) => {
      toast.error("Failed to save policy. Please try again.");
    },
  });

  const handleSavePolicy = () => {
    const errors = validateForm(formData);
    setTouched({
      title: true,
      category: true,
      description: true,
      effectiveDate: true,
      status: true,
    });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the highlighted errors");
      return;
    }

    const policyData = {
      title: formData.title.trim(),
      category: formData.category,
      description: formData.description,
      effectiveDate: formData.effectiveDate,
      status: formData.status,
    };

    savePolicyMutation.mutate({
      policyId: editingPolicy?.id || editingPolicy?._id,
      policyData,
    });
  };

  const handleDeletePolicy = (policy) => {
    setPolicyToDelete(policy);
    setShowDeleteModal(true);
  };

  // React Query: Delete policy mutation
  const deletePolicyMutation = useMutation({
    mutationFn: async (policyId) => {
      return await policyService.deletePolicy(policyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['acknowledgmentStats'] });
      toast.success("Policy deleted successfully");
      setShowDeleteModal(false);
      setPolicyToDelete(null);
    },
    onError: (error) => {
      toast.error("Failed to delete policy. Please try again.");
    },
  });

  const confirmDeletePolicy = () => {
    if (!policyToDelete) return;
    deletePolicyMutation.mutate(policyToDelete.id);
  };

  const cancelDeletePolicy = () => {
    setShowDeleteModal(false);
    setPolicyToDelete(null);
  };

  // Bulk delete policies handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows);
    let successCount = 0;
    let failCount = 0;

    for (const id of ids) {
      try {
        await policyService.deletePolicy(id);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} policy(ies) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['acknowledgmentStats'] });
    } else {
      toast.error('Failed to delete selected policies');
    }
  };

  const handleViewDetails = (policy) => {
    setSelectedPolicy(policy);
    setShowDetails(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-600 bg-green-100";
      case "draft":
        return "text-yellow-600 bg-yellow-100";
      case "archived":
        return "text-gray-600 bg-gray-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  // formatDate is now imported from stringUtils

  const getAcknowledmentProgress = (policy) => {
    if (!policy?.acknowledgmentRequired) return 100;
    const acknowledged = Number(policy?.acknowledgmentCount || policy?.acknowledgedEmployees || 0);
    const total = Number(policy?.totalEmployees || 0);
    if (!total || total <= 0) return 0;
    return Math.round((acknowledged / total) * 100);
  };

  const mapPolicyForExport = (policy) => ({
    'Title': policy.title || '',
    'Category': policy.category || '',
    'Version': policy.version || '1.0',
    'Status': policy.status || '',
    'Effective Date': formatDate(policy.effectiveDate, { includeTime: false }),
    'Created Date': formatDate(policy.createdAt, { includeTime: false }),
    'Total Staff': policy.totalEmployees || 0,
    'Acknowledged Employees': policy.acknowledgmentCount || policy.acknowledgedEmployees || 0,
    'Acknowledgment Rate (%)': `${getAcknowledmentProgress(policy)}%`,
    'Created By': policy.createdBy || '',
    'Approved By': policy.approvedBy || '',
    'Last Updated': formatDate(policy.lastUpdated || policy.updatedAt, { includeTime: false }),
    'PDF URL': typeof (policy.pdfFilePath || policy.pdfUrl) === 'object' ? (policy.pdfFilePath || policy.pdfUrl)?.url : (policy.pdfFilePath || policy.pdfUrl) || ''
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredPolicies.map(p => p.id || p._id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportSelected = () => {
    const selectedData = filteredPolicies
      .filter(p => selectedRows.has(p.id || p._id))
      .map(mapPolicyForExport);

    exportToCSV(
      selectedData,
      'policies_selected',
      { title: 'Policy Library Export', type: 'Selected Policies' }
    );
  };

  const handleExportCurrentPage = () => {
    const pageData = filteredPolicies.map(mapPolicyForExport);
    exportToCSV(
      pageData,
      'policies_current_page',
      { title: 'Policy Library Export', type: 'Current Page Data' }
    );
  };

  const handleExportRange = async (startDate, endDate) => {
    try {
      setExportLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const queryParams = new URLSearchParams({
        category: filterCategory,
        status: filterStatus,
        search: searchTerm,
        startDate,
        endDate
      });

      await downloadFromBackend(
        `${API_BASE_URL}/policies/export?${queryParams.toString()}`,
        generateExportFilename('policies_range'),
        { 'x-user-role': userRole }
      );
      setShowExportModal(false);
      toast.success('✅ Export started successfully!');
    } catch (error) {
      toast.error('Failed to export policies');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    try {
      setExportLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const queryParams = new URLSearchParams({
        category: filterCategory,
        status: filterStatus,
        search: searchTerm
      });

      await downloadFromBackend(
        `${API_BASE_URL}/policies/export?${queryParams.toString()}`,
        generateExportFilename('policies_all'),
        { 'x-user-role': userRole }
      );
      setShowExportModal(false);
      toast.success('✅ Export started successfully!');
    } catch (error) {
      toast.error('Failed to export policies');
    } finally {
      setExportLoading(false);
    }
  };



  if (showDetails && selectedPolicy) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 min-w-0">
              <PermissionGate permission="compliance_policy" action="read">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDetails(false)}
                  className="flex items-center space-x-2 flex-shrink-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </PermissionGate>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold text-gray-900 break-words">
                  {selectedPolicy.title}
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                  selectedPolicy.status
                )}`}
              >
                {(selectedPolicy.status || "").charAt(0).toUpperCase() +
                  (selectedPolicy.status || "").slice(1)}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - Left Side */}
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Description
                </h3>
                <div className="ql-snow rounded-lg border border-gray-100 bg-gray-50/50">
                  <div
                    className={`ql-editor !border-0 !p-4 !min-h-0 ${RICH_TEXT_DISPLAY_CLASS}`}
                    dangerouslySetInnerHTML={{
                      __html: prepareDescriptionForDisplay(selectedPolicy.description),
                    }}
                  />
                </div>
              </div>

              {/* Policy Content */}
              {selectedPolicy.content && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Policy Content
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed break-words">
                      {selectedPolicy.content}
                    </p>
                  </div>
                </div>
              )}


              {/* Tags */}
              {selectedPolicy.tags && normalizeTags(selectedPolicy.tags).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {normalizeTags(selectedPolicy.tags).map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Right Side */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Basic Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Category
                    </label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {selectedPolicy.category || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Status
                    </label>
                    <p className="text-sm mt-1">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          selectedPolicy.status
                        )}`}
                      >
                        {(selectedPolicy.status || "").charAt(0).toUpperCase() +
                          (selectedPolicy.status || "").slice(1)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Effective Date
                    </label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {selectedPolicy.effectiveDate
                        ? formatDate(selectedPolicy.effectiveDate, { includeTime: false })
                        : "N/A"}
                    </p>
                  </div>
                  {selectedPolicy.acknowledgmentDeadline && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Acknowledgment Deadline
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {formatDate(selectedPolicy.acknowledgmentDeadline, {
                          includeTime: false,
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Acknowledgment Status */}
              {selectedPolicy.acknowledgmentRequired && (
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Acknowledgment Status
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-semibold text-gray-900">
                          {getAcknowledmentProgress(selectedPolicy)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${getAcknowledmentProgress(selectedPolicy) >= 95
                            ? "bg-green-500"
                            : getAcknowledmentProgress(selectedPolicy) >= 85
                              ? "bg-yellow-500"
                              : "bg-red-500"
                            }`}
                          style={{
                            width: `${getAcknowledmentProgress(selectedPolicy)}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                      <div>
                        <p className="text-xs text-gray-500">Acknowledged</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {selectedPolicy.acknowledgmentCount || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total Staff</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {selectedPolicy.totalEmployees || 0}
                        </p>
                      </div>
                    </div>
                    {selectedPolicy.acknowledgmentRate !== undefined && (
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500">Acknowledgment Rate</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {selectedPolicy.acknowledgmentRate.toFixed(1)}%
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Applicable To */}
              {selectedPolicy.applicableTo &&
                selectedPolicy.applicableTo.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Applicable To
                    </h3>
                    <div className="space-y-2">
                      {selectedPolicy.applicableTo.map((role, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2 p-2 bg-white rounded-md"
                        >
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700 break-words flex-1 min-w-0">{role}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Metadata */}
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Metadata
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Created By
                    </label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {selectedPolicy.createdBy || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Created At
                    </label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {selectedPolicy.createdAt ? formatDate(selectedPolicy.createdAt, { includeTime: false }) : "N/A"}
                    </p>
                  </div>
                  {selectedPolicy.updatedBy && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Last Updated By
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {selectedPolicy.updatedBy}
                      </p>
                    </div>
                  )}
                  {selectedPolicy.createdAt && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Created At
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {formatDate(selectedPolicy.createdAt, { includeTime: true })}
                      </p>
                    </div>
                  )}
                  {selectedPolicy.updatedAt && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Last Updated
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {formatDate(selectedPolicy.updatedAt, { includeTime: true })}
                      </p>
                    </div>
                  )}
                  {selectedPolicy.lastUpdated && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Last Updated Date
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {formatDate(selectedPolicy.lastUpdated, { includeTime: true })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                {(userRole === "super_admin" || userRole === "hr_manager") && (
                  <PermissionGate permission="compliance_policy" action="update">
                    <Button
                      variant="outline"
                      className="w-full flex items-center justify-center space-x-2"
                      onClick={() => handleEditPolicy(selectedPolicy)}
                    >
                      <Edit className="h-4 w-4" />
                      <span>Edit Policy</span>
                    </Button>
                  </PermissionGate>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showPolicyForm) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Simple Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">
              {editingPolicy ? "Edit Policy" : "Create New Policy"}
            </h1>
            <PermissionGate permission="compliance_policy" action="read">
              <Button
                variant="outline"
                onClick={() => setShowPolicyForm(false)}
                className="flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Basic Information Section */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Policy Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                    setFormData({ ...formData, title: value });
                  }}
                  maxLength={100}
                  required
                  onBlur={() => handleBlur("title")}
                  className={`input-field ${(touched.title || savePolicyMutation.isPending) && formErrors.title
                    ? "border-red-500 bg-red-50"
                    : ""
                    }`}
                  placeholder="Enter policy title"
                />
                <div className="flex items-center justify-between mt-1">
                  <div>
                    {(touched.title || savePolicyMutation.isPending) && formErrors.title && (
                      <p className="text-xs text-red-600">{formErrors.title}</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {formData.title.length}/100 characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  onBlur={() => handleBlur("category")}
                  className={`input-field bg-white ${(touched.category || savePolicyMutation.isPending) && formErrors.category
                    ? "border-red-500 bg-red-50"
                    : ""
                    }`}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => {
                    // Extract the category name (before the comment)
                    const categoryName = category.split('//')[0].trim();
                    return (
                      <option key={categoryName} value={categoryName}>
                        {categoryName}
                      </option>
                    );
                  })}
                </select>
                {(touched.category || savePolicyMutation.isPending) && formErrors.category && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.category}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brief Description <span className="text-red-500">*</span>
                </label>
                <div
                  className={`rounded-lg border-2 overflow-hidden ${(touched.description || savePolicyMutation.isPending) && formErrors.description
                    ? "border-red-500 ring-2 ring-red-500"
                    : "border-gray-300"
                    }`}
                >
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => {
                      const plainText = getPlainTextFromHtml(value);
                      const isOverLimit = plainText.length > DESCRIPTION_MAX_CHARS;
                      const nextDescription = isOverLimit
                        ? truncateHtmlToPlainTextLimit(value, DESCRIPTION_MAX_CHARS)
                        : value;

                      setFormData((prev) => ({ ...prev, description: nextDescription }));

                      if (isOverLimit) {
                        toast.error(`Description exceeded ${DESCRIPTION_MAX_CHARS} characters. Extra text was removed.`);
                      }
                    }}
                    maxLength={DESCRIPTION_MAX_CHARS}
                    blockWhenMaxExceeded={false}
                    onBlur={() => handleBlur("description")}
                    placeholder="Brief description of the policy"
                    height="220px"
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <div>
                    {(touched.description || savePolicyMutation.isPending) && formErrors.description && (
                      <p className="text-xs text-red-600">{formErrors.description}</p>
                    )}
                  </div>
                  <p
                    className={`text-xs ${getPlainTextFromHtml(formData.description).length > DESCRIPTION_MAX_CHARS
                      ? "text-red-600 font-medium"
                      : "text-gray-500"
                      }`}
                  >
                    {getPlainTextFromHtml(formData.description).length}/{DESCRIPTION_MAX_CHARS} characters
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Effective Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.effectiveDate}
                    min={originalEffectiveDate || new Date().toISOString().split('T')[0]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        effectiveDate: e.target.value,
                      })
                    }
                    onBlur={() => handleBlur("effectiveDate")}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    className={`input-field ${(touched.effectiveDate || savePolicyMutation.isPending) && formErrors.effectiveDate
                      ? "border-red-500 bg-red-50"
                      : ""
                      }`}
                  />
                  {(touched.effectiveDate || savePolicyMutation.isPending) && formErrors.effectiveDate && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.effectiveDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    onBlur={() => handleBlur("status")}
                    className={`input-field bg-white ${(touched.status || savePolicyMutation.isPending) && formErrors.status
                      ? "border-red-500 bg-red-50"
                      : ""
                      }`}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {(touched.status || savePolicyMutation.isPending) && formErrors.status && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.status}</p>
                  )}
                </div>
              </div>


            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end space-x-3">
            <PermissionGate permission="compliance_policy" action="read">
              <button
                className="btn-cancel"
                onClick={() => setShowPolicyForm(false)}
              >
                Cancel
              </button>
            </PermissionGate>
            <PermissionGate permission="compliance_policy" action={editingPolicy ? "update" : "create"}>
              <button
                onClick={handleSavePolicy}
                disabled={savePolicyMutation.isPending}
                className="btn-create flex items-center space-x-2"
              >
                {savePolicyMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>
                      {editingPolicy ? "Update Policy" : "Create Policy"}
                    </span>
                  </>
                )}
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Policy Library</h1>
              <p className="text-gray-600">Manage company policies and documentation</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <PermissionGate permission="compliance_policy" action="read">
              <Button
                variant="outline"
                className="flex items-center space-x-2"
                onClick={selectedRows.size > 0 ? handleExportSelected : handleExport}
              >
                <Download className="h-4 w-4" />
                <span>{selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}</span>
              </Button>
            </PermissionGate>
            {selectedRows.size > 0 && (
              <PermissionGate permission="compliance_policy" action="delete">
                <Button
                  variant="danger"
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white rounded-md min-w-[140px]"
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  loading={bulkDeleteLoading}
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete Selected ({selectedRows.size})</span>
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="compliance_policy" action="create">
              <Button
                variant="outline"
                onClick={() => setShowBulkUpload(true)}
                className="btn-outline flex items-center space-x-2"
              >
                <Upload className="h-4 w-4" />
                <span>Bulk Upload</span>
              </Button>
            </PermissionGate>
            <PermissionGate permission="compliance_policy" action="create">
              <Button
                onClick={handleCreatePolicy}
                className="btn-create flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Create Policy</span>
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      <div className="p-6">
        {isErrorLoading && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-red-700">
                {loadingError?.message || "Failed to load policies. Please try again."}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4 w-full">
            {/* Search Bar */}
            <div className="relative flex-[2] min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search Policy Name..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                maxLength={100}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Category Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            {/* Created Date Filter */}
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onChange={setDateRange}
                containerClassName="flex items-center space-x-2"
                inputClassName="text-sm w-36 cursor-pointer px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
              />
            </div>

            {/* Select All */}
            <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="select-all"
                checked={filteredPolicies.length > 0 && selectedRows.size === filteredPolicies.length}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-brand focus:ring-brand focus:ring-1 h-4 w-4"
              />
              <label htmlFor="select-all" className="text-sm font-medium text-gray-700 cursor-pointer">
                Select All
              </label>
            </div>

            {/* Clear Filters Button */}
            {(searchTerm || filterCategory !== 'all' || filterStatus !== 'all' || startDate || endDate || selectedRows.size > 0) && (
              <div className="relative flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => {
                    resetPolicyFilters();
                    setSelectedRows(new Set());
                  }}
                  className="flex items-center space-x-2"
                >
                  <X className="h-4 w-4" />
                  <span>Clear</span>
                </Button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin text-agrosaf-green" />
              <span className="text-gray-600 font-medium">Loading policies...</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(filteredPolicies || []).map((policy) => (
              <div
                key={policy.id || policy._id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-200 bg-white hover:border-agrosaf-green/30"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(policy.id || policy._id)}
                      onChange={() => handleSelectRow(policy.id || policy._id)}
                      className="rounded border-gray-300 text-brand focus:ring-brand focus:ring-1 h-4 w-4 mr-2"
                    />
                    <FileText className="h-5 w-5 text-[#0F2942]" />
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(policy.status)}`}>
                      {(policy.status || "").charAt(0).toUpperCase() + (policy.status || "").slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <PermissionGate permission="compliance_policy" action="read">
                      <Button variant="ghost" size="sm" onClick={() => handleViewDetails(policy)} className="p-2 hover:bg-blue-50 text-[#0F2942] rounded-md" title="View Details">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </PermissionGate>
                    <PermissionGate permission="compliance_policy" action="update">
                      <Button variant="ghost" size="sm" onClick={() => handleEditPolicy(policy)} className="p-2 hover:bg-green-50 text-agrosaf-green rounded-md" title="Edit Policy">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </PermissionGate>
                    <PermissionGate permission="compliance_policy" action="delete">
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePolicy(policy)} className="p-2 hover:bg-red-50 text-red-600 rounded-md" title="Delete Policy">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </PermissionGate>
                  </div>
                </div>

                <h3 className="font-semibold text-gray-900 mb-2 truncate" title={policy.title}>{policy.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {getPlainTextFromHtml(policy.description || "")}
                </p>

                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center justify-between">
                    <span>Category:</span>
                    <span className="font-medium text-gray-700">{policy.category}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Effective:</span>
                    <span className="font-medium text-gray-700">{formatDate(policy.effectiveDate, { includeTime: false })}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Created Date:</span>
                    <span className="font-medium text-gray-700">{formatDate(policy.createdAt, { includeTime: true })}</span>
                  </div>
                  {policy.acknowledgmentRequired && (
                    <div className="mt-3 p-2 bg-gray-50 rounded-md">
                      <div className="flex justify-between text-xs mb-2">
                        <span className="font-medium text-gray-700">Acknowledgments</span>
                        <span className="font-semibold text-agrosaf-green">{getAcknowledmentProgress(policy)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-agrosaf-green to-agrosaf-darkGreen h-2 rounded-full transition-all duration-300"
                          style={{ width: `${getAcknowledmentProgress(policy)}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {policy.acknowledgmentCount} of {policy.totalEmployees} employees
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {(policy.tags || []).slice(0, 3).map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-50 text-[#0F2942] rounded-full text-xs font-medium border border-blue-100">#{tag}</span>
                  ))}
                  {(policy.tags || []).length > 3 && (
                    <span className="px-2 py-1 bg-gray-50 text-gray-500 rounded-full text-xs font-medium border border-gray-100">+{(policy.tags || []).length - 3}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && (filteredPolicies || []).length > 0 && pagination && (
          <div className="mt-6 bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <PaginationControls
              currentPage={page}
              totalPages={pagination.totalPages || 0}
              totalItems={pagination.total || 0}
              limit={limit}
              onPageChange={(newPage) => setPage(newPage)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
              }}
              hasNextPage={page < (pagination.totalPages || 0)}
              hasPrevPage={page > 1}
              border={false}
            />
          </div>
        )}

        {!loading && (filteredPolicies || []).length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No policies found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterCategory !== "all" || filterStatus !== "all" || startDate || endDate
                ? "Try adjusting your search or filter criteria."
                : "Get started by creating your first policy."}
            </p>
          </div>
        )}
      </div>

      {
        showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-yellow-50 rounded-lg">
                      <AlertCircle className="h-6 w-6 text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
                  </div>
                  <button onClick={cancelDeletePolicy} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600">Are you sure you want to delete this policy? This action cannot be undone.</p>
                  {policyToDelete && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{policyToDelete.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{policyToDelete.category}</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <Button variant="outline" onClick={cancelDeletePolicy} disabled={deletePolicyMutation.isPending}>
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={confirmDeletePolicy} disabled={deletePolicyMutation.isPending} className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]">
                    {deletePolicyMutation.isPending ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span>Deleting...</span>
                      </div>
                    ) : (
                      "Delete Policy"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        title="Export Policy Library"
        loading={exportLoading}
      />

      {/* Bulk Delete Confirmation Modal */}
      {
        showBulkDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <Trash2 className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Confirm Bulk Deletion</h3>
                  </div>
                  <button
                    onClick={() => setShowBulkDeleteConfirm(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600">
                    Are you sure you want to delete <span className="font-bold text-red-600">{selectedRows.size}</span> selected policies?
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    This action cannot be undone and will permanently remove all selected policy data.
                  </p>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <Button
                    variant="outline"
                    onClick={() => setShowBulkDeleteConfirm(false)}
                    disabled={bulkDeleteLoading}
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="compliance_policy" action="delete">
                    <Button
                      variant="danger"
                      onClick={handleBulkDelete}
                      disabled={bulkDeleteLoading}
                      className="bg-red-600 hover:bg-red-700 text-white min-w-[120px]"
                    >
                      {bulkDeleteLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          <span>Deleting...</span>
                        </div>
                      ) : (
                        `Delete ${selectedRows.size} Policies`
                      )}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {showBulkUpload && (
        <BulkUploadModal
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['policies'] })}
          config={{
            title: "Bulk Upload Policies",
            subtitle: "Import multiple company policies using a CSV file",
            templateEndpoint: "/policies/bulk-upload/template",
            validateEndpoint: "/policies/bulk-upload/validate",
            uploadEndpoint: "/policies/bulk-upload",
            templateFilename: "policy_bulk_upload_template.csv",
            dataKey: "data",
            maxRecords: 100,
            instructions: [
              "Title and Category are required.",
              "Description is required (minimum 20 characters).",
              "Effective Date is required (YYYY-MM-DD format).",
              "Status is optional (defaults to 'draft'). Values: draft, active, inactive.",
              "PDF URL is optional.",
              "Acknowledgment Required is optional (TRUE/FALSE, defaults to TRUE)."
            ]
          }}
        />
      )}
    </div >
  );
};

export default PolicyLibrary;
